import { FetchResult, useMutation } from '@apollo/client';
import { useToast } from '@tradener/lumen';

import UPLOAD_OPERATION_LIMITS, { ResponseProps, PayloadProps } from '@graphql/mutation/operationLimits/upload';

interface UploadOperationLimitsProps {
  upload: (payload: { variables: PayloadProps }) => Promise<FetchResult<ResponseProps, Record<string, any>, Record<string, any>>>;
  loading: boolean;
}

const useUploadOperationLimits = () => {
  const toast = useToast();

  const [upload, { loading }] = useMutation<ResponseProps, PayloadProps>(UPLOAD_OPERATION_LIMITS, {
    onCompleted: ({ importOperationLimits }) => {
      const hasErrors = (importOperationLimits?.errors?.length || 0) > 0;

      if (hasErrors) {
        toast({
          title: 'Importação parcial',
          status: 'warning',
          description: importOperationLimits.errors.join(' '),
        });

        return;
      }

      toast({ title: 'Sucesso', status: 'success', description: 'Upload realizado com sucesso!' });
    },
    onError: (error) => {
      const graphQLErrors = error.graphQLErrors ?? [];
      const normalizeMessage = (message?: string) => {
        if (!message) {
          return '';
        }

        return message
          .replace(/^GraphQL error:\s*/i, '')
          .trim()
          .toLowerCase();
      };

      const isFallbackWarningMessage = (message?: string) => {
        if (!message) {
          return false;
        }

        const normalizedMessage = normalizeMessage(message);

        if (normalizedMessage.startsWith('salvo ')) {
          return true;
        } else if (normalizedMessage.includes('linhas') && normalizedMessage.includes('inválidas')) {
          return true;
        } else {
          return false;
        }
      };

      const warningLikeErrors = graphQLErrors.filter((graphQLError) => {
        if (graphQLError.extensions?.code === 'WARNING') {
          return true;
        }

        if (!graphQLError.extensions?.code && isFallbackWarningMessage(graphQLError.message)) {
          return true;
        }

        return false;
      });
      const warningMessages = warningLikeErrors
        .map((graphQLError) => graphQLError.message)
        .filter(Boolean)
        .join(' ');
      let fallbackWarningMessage = '';

      if (!warningMessages && isFallbackWarningMessage(error.message)) {
        fallbackWarningMessage = error.message.replace(/^GraphQL error:\s*/i, '').trim();
      }

      const hasWarningMessages = !!warningMessages;
      const hasFallbackWarningMessage = !!fallbackWarningMessage;

      if (hasWarningMessages) {
        toast({
          title: 'Importação parcial',
          status: 'warning',
          description: warningMessages,
        });

        return;
      } else if (hasFallbackWarningMessage) {
        toast({
          title: 'Importação parcial',
          status: 'warning',
          description: fallbackWarningMessage,
        });

        return;
      }

      toast({ title: 'Erro', status: 'error', description: error.message || 'Erro ao realizar upload!' });
    },
  });

  return {
    upload,
    loading,
  } as UploadOperationLimitsProps;
};

export default useUploadOperationLimits;
